import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Book, Mail, Trash2, Edit } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const AdminStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedStory, setSelectedStory] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const userFilter = searchParams.get('user') || searchParams.get('userId');
  const userName = searchParams.get('name');

  const getErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'Cannot reach the server. Please check your connection.';
    }
    return error?.message || fallbackMessage;
  };

  const fetchStories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/stories', {
        params: { page, limit: 10, search, userId: userFilter || undefined },
      });
      if (response.data.success) {
        setStories(response.data.stories);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load stories'));
    } finally {
      setLoading(false);
    }
  }, [page, search, userFilter]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const handleDelete = async (storyId) => {
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/stories/${storyId}`);
      if (response.data.success) {
        toast.success('Story deleted successfully');
        fetchStories();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete story'));
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (story) => {
    setSelectedStory({
      ...story,
      title: story.title || '',
      theme: story.theme || '',
      content: story.content || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateStory = async () => {
    if (!selectedStory) return;
    try {
      setSaving(true);
      const response = await api.put(`/admin/stories/${selectedStory.id}`, {
        title: selectedStory.title?.trim(),
        theme: selectedStory.theme?.trim(),
        content: selectedStory.content,
      });
      if (response.data.success) {
        toast.success('Story updated successfully');
        setShowEditModal(false);
        fetchStories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update story');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (story) => {
    setDeleteTarget(story);
    setShowConfirmModal(true);
  };

  return (
    <AdminSidebar>
        <div className="mb-8">
          <p className="text-gray-600">
            {userFilter
              ? `Viewing stories created by ${userName || 'selected user'}`
              : 'Manage all stories on the platform'}
          </p>
          {userFilter && (
            <button
              onClick={() => navigate('/admin/stories')}
              className="mt-3 inline-flex items-center px-4 py-2 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 text-sm font-semibold"
            >
              Clear User Filter
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search stories by title, content, character, or user..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : stories.length === 0 ? (
          <EmptyState
            icon={<Book className="h-7 w-7" />}
            title={
              search
                ? `No stories match “${search}”`
                : 'No stories yet'
            }
            description={
              search
                ? 'No stories match your search. Clear the filter to see all stories.'
                : 'As families create stories, they will appear here so you can monitor and curate content.'
            }
            action={
              search
                ? {
                    label: 'Clear search',
                    onClick: () => {
                      setSearch('');
                      setPage(1);
                    },
                  }
                : undefined
            }
          />
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Story</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">Character</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">User</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Theme</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">Created</th>
                      <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {stories.map((story) => (
                      <tr key={story.id} className="hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-pink-50/50 transition-all duration-150 group">
                        <td className="px-4 sm:px-6 py-4">
                          <div className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-1">{story.title}</div>
                          <div className="text-xs sm:text-sm text-gray-500 line-clamp-2 mt-1">
                            {story.content?.substring(0, 120)}...
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                          <span className="text-sm font-medium text-gray-900">{story.character_name}</span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                          <div className="text-sm font-medium text-gray-900">{story.user_first_name}</div>
                          <div className="text-xs text-gray-500 flex items-center truncate">
                            <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{story.user_email}</span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            {story.theme || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                          <div className="text-sm text-gray-600">
                            {new Date(story.created_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => openEditModal(story)}
                              className="p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm(story)}
                              className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.pages > 1 && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                    <span className="font-semibold">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-semibold">{pagination.total}</span> stories
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      title="First page"
                    >
                      ««
                    </button>
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                        let pageNum;
                        if (pagination.pages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= pagination.pages - 2) {
                          pageNum = pagination.pages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              page === pageNum
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === pagination.pages}
                      className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setPage(pagination.pages)}
                      disabled={page === pagination.pages}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      title="Last page"
                    >
                      »»
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      {/* Edit Story Modal */}
      {showEditModal && selectedStory && (
        <Modal title="Edit Story" onClose={() => setShowEditModal(false)} width="max-w-3xl">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={selectedStory.title}
                onChange={(e) =>
                  setSelectedStory((prev) => ({ ...prev, title: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
              <input
                type="text"
                value={selectedStory.theme || ''}
                onChange={(e) =>
                  setSelectedStory((prev) => ({ ...prev, theme: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                rows="6"
                value={selectedStory.content}
                onChange={(e) =>
                  setSelectedStory((prev) => ({ ...prev, content: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-6">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateStory}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {showConfirmModal && deleteTarget && (
        <ConfirmDialog
          title="Delete Story"
          description={`Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={() => {
            setShowConfirmModal(false);
            handleDelete(deleteTarget.id);
          }}
          onCancel={() => {
            setShowConfirmModal(false);
            setDeleteTarget(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminStories;

